import React, { useCallback, useEffect, useState } from "react";
import { Bike, MapPin, Phone, Package, PackageCheck, RefreshCw, Store, IndianRupee, CheckCircle2, Navigation, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatusBadge, StatCard } from "../../components/app/ui";
import { useAuth } from "../../context/AuthContext";
import { driverApi, foodApi, inr, formatDate, errorMessage, titleCase } from "../../lib/api";
import { DriverHistoryDialog } from "../../components/app/HistoryDetailDialog";

const TABS = [
  { key: "available", label: "Available" },
  { key: "active", label: "My deliveries" },
  { key: "past", label: "Past" },
];

function DeliveryCard({ o, tab, onClaim, onStatus, onReached, onView, busyId }) {
  const busy = busyId === o.order_id;
  return (
    <div className="kt-card p-5" data-testid={`delivery-${o.order_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-ink">#{o.order_id.slice(-6).toUpperCase()}</p>
            <StatusBadge status={o.status} />
          </div>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-ink"><Store className="h-4 w-4 text-brand" /> {o.restaurant_name}</p>
          {o.restaurant_address && <p className="mt-0.5 text-xs text-ink-muted">{o.restaurant_address}</p>}
        </div>
        <p className="inline-flex items-center gap-1 text-lg font-bold text-graphite" title="Your delivery fee"><IndianRupee className="h-4 w-4" />{(o.delivery_fee || 0).toFixed(0)}</p>
      </div>

      <div className="mt-3 space-y-1.5 rounded-xl bg-surface-100 p-3 text-sm">
        <p className="flex items-start gap-2 text-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {o.delivery_address}</p>
        <p className="text-xs text-ink-soft">{o.items?.map((i) => `${i.qty} × ${i.name}`).join(", ")}</p>
        <p className="text-xs text-ink-muted">Order {inr(o.total)} · {o.payment_status === "paid" ? "Prepaid" : "Collect on delivery"}</p>
      </div>

      {o.notes && <p className="mt-3 rounded-xl bg-brand-50 px-3 py-2 text-sm text-ink">Note: {o.notes}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {tab !== "past" && Number.isFinite(o.restaurant_lat) && Number.isFinite(o.restaurant_lng) && (
          <Button asChild variant="outline" className="rounded-full border-brand text-brand hover:bg-brand-50">
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${o.restaurant_lat},${o.restaurant_lng}`} target="_blank" rel="noreferrer" data-testid={`nav-restaurant-${o.order_id}`}><Navigation className="mr-2 h-4 w-4" /> Navigate to restaurant</a>
          </Button>
        )}
        {tab === "active" && o.status === "out_for_delivery" && Number.isFinite(o.delivery_lat) && Number.isFinite(o.delivery_lng) && (
          <Button asChild variant="outline" className="rounded-full"><a href={`https://www.google.com/maps/dir/?api=1&destination=${o.delivery_lat},${o.delivery_lng}`} target="_blank" rel="noreferrer" data-testid={`nav-customer-${o.order_id}`}><Navigation className="mr-2 h-4 w-4" /> Navigate to customer</a></Button>
        )}
        {tab === "available" && <Button onClick={() => onClaim(o)} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`claim-${o.order_id}`}><Package className="mr-2 h-4 w-4" /> Accept delivery</Button>}
        {tab === "active" && ["accepted", "preparing", "ready"].includes(o.status) && (
          o.driver_reached_restaurant_at
            ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700" data-testid={`reached-restaurant-done-${o.order_id}`}><CheckCircle2 className="h-4 w-4" /> Checked in at restaurant</span>
            : <Button onClick={() => onReached(o)} disabled={busy} variant="outline" className="rounded-full border-brand text-brand hover:bg-brand-50" data-testid={`reached-restaurant-${o.order_id}`}><Store className="mr-2 h-4 w-4" /> Arrived at Restaurant</Button>
        )}
        {tab === "active" && o.status === "ready" && <Button onClick={() => onStatus(o, "out_for_delivery")} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`pickup-${o.order_id}`}><Navigation className="mr-2 h-4 w-4" /> Mark picked up</Button>}
        {tab === "active" && o.status === "out_for_delivery" && <Button onClick={() => onStatus(o, "delivered")} disabled={busy} className="rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid={`deliver-${o.order_id}`}><PackageCheck className="mr-2 h-4 w-4" /> Mark delivered</Button>}
        {tab === "past" && <span className="inline-flex items-center gap-1.5 text-sm text-graphite"><CheckCircle2 className="h-4 w-4" /> Delivered {o.delivered_at ? formatDate(o.delivered_at) : ""}</span>}
        {(o.restaurant_phone && tab === "active") && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${o.restaurant_phone}`} data-testid={`call-restaurant-${o.order_id}`}><Store className="mr-2 h-4 w-4" /> Call restaurant · {o.restaurant_phone}</a></Button>}
        {(o.customer_phone && tab === "active") && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${o.customer_phone}`} data-testid={`call-${o.order_id}`}><Phone className="mr-2 h-4 w-4" /> Call customer · {o.customer_phone}</a></Button>}
        {tab !== "available" && <Button variant="outline" className="rounded-full" onClick={() => onView(o)} data-testid={`view-delivery-${o.order_id}`}><Eye className="mr-2 h-4 w-4" /> View</Button>}
      </div>
    </div>
  );
}

export default function Deliveries() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("available");
  const [busyId, setBusyId] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const load = useCallback(() => driverApi.deliveries().then(setData).catch(() => setData({ available: [], active: [], past: [] })), []);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  // Share live GPS with customers while there's an active delivery in progress.
  const hasActive = (data?.active || []).some((o) => o.status === "out_for_delivery" || o.status === "ready");
  useEffect(() => {
    if (!hasActive || !navigator.geolocation) return;
    let last = 0;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - last < 8000) return;
        last = now;
        driverApi.postLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading ?? undefined).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [hasActive]);

  const verified = user?.verification_status === "verified";

  const claim = async (o) => {
    setBusyId(o.order_id);
    try { await driverApi.claimDelivery(o.order_id); toast.success("Delivery accepted"); setTab("active"); await load(); }
    catch (e) { toast.error(errorMessage(e)); await load(); } finally { setBusyId(null); }
  };
  const setStatus = async (o, status) => {
    setBusyId(o.order_id);
    try { await foodApi.orderStatus(o.order_id, status); toast.success(`Marked ${titleCase(status.replace(/_/g, " "))}`); await load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };
  const reached = async (o) => {
    setBusyId(o.order_id);
    try { await foodApi.reachedRestaurant(o.order_id); toast.success("Checked in — restaurant & customer notified"); await load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  const list = data?.[tab] || [];

  return (
    <>
      <PageHeader title="Deliveries" description="Accept ready orders, pick them up, and mark them delivered." actions={<Button variant="outline" onClick={load} className="rounded-full" data-testid="deliveries-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>} />

      {!verified && (
        <div className="mb-6 rounded-2xl border border-brand-200 bg-brand-50 px-5 py-3 text-sm text-ink" data-testid="deliveries-kyc">Your driver profile isn't verified yet — complete KYC to start accepting deliveries.</div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Available" value={data?.available.length ?? "…"} icon={Package} />
        <StatCard label="My deliveries" value={data?.active.length ?? "…"} icon={Bike} tone="brand" />
        <StatCard label="Delivered" value={data?.past.length ?? "…"} icon={PackageCheck} tone="graphite" />
      </div>

      <div className="mb-5 flex gap-2" data-testid="delivery-tabs">
        {TABS.map((t) => <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-ink text-surface" : "bg-white text-ink-soft ring-1 ring-surface-300 hover:text-ink"}`} data-testid={`delivery-tab-${t.key}`}>{t.label} <span className="ml-1 opacity-70">{data?.[t.key].length ?? 0}</span></button>)}
      </div>

      {data === null && <LoadingBlock rows={3} />}
      {data && list.length === 0 && <EmptyState icon={Bike} title={tab === "available" ? "No deliveries available" : tab === "active" ? "No active deliveries" : "No past deliveries"} text={tab === "available" ? "Ready orders from restaurants appear here." : "Accepted deliveries will show up here."} />}
      <div className="grid gap-4">
        {list.map((o) => <DeliveryCard key={o.order_id} o={o} tab={tab} onClaim={claim} onStatus={setStatus} onReached={reached} onView={(ord) => setViewItem({ kind: "food", id: ord.order_id })} busyId={busyId} />)}
      </div>
      <DriverHistoryDialog item={viewItem} fetcher={(kind, id) => (kind === "ride" ? driverApi.rideDetail(id) : driverApi.orderDetail(id))} onClose={() => setViewItem(null)} />
    </>
  );
}
